import axios from '@/tools/Axios/axios'
import type { IPaymentTerminal, IRevolutTransaction } from '@/interfaces/IPaymentTerminal'

export interface RevolutLineItem {
    name: string
    quantity: number
    unit_price_amount: number
    total_amount: number
    taxes?: { name: string; amount: number }[]
}

export interface InitiatePayload {
    orderId: string
    terminalId: string
    amount: number
    currency: string
    /**
     * Optionnel : l'API construit les lineItems depuis l'Order si non fourni
     * (cas standard guichet). Conserve pour rétro-compat externe.
     */
    lineItems?: RevolutLineItem[]
}

export interface ManualFallbackPayload {
    orderId: string
    method: 'cash' | 'creditCardExternal' | 'check'
    reason?: string
}

const BASE_TERMINALS = '/payment-terminals'
const BASE_PAYMENTS = '/payments/terminal'

export const paymentTerminalService = {
    async getAvailable(): Promise<IPaymentTerminal[]> {
        // L'API renvoie { items: PaymentTerminal[], total: number }
        // (pas un array a la racine, cf payment-terminals.controller cote API)
        const { data } = await axios.get<{
            items: IPaymentTerminal[]
            total: number
        }>(`${BASE_TERMINALS}/available`)
        return data?.items ?? []
    },

    async initiate(payload: InitiatePayload): Promise<IRevolutTransaction> {
        const { data } = await axios.post(`${BASE_PAYMENTS}/initiate`, payload)
        return data
    },

    async getStatus(transactionId: string): Promise<IRevolutTransaction> {
        // skipLoader: ne pas declencher le loader global a chaque poll (1.5s)
        const { data } = await axios.get(`${BASE_PAYMENTS}/${transactionId}/status`, {
            skipLoader: true,
        } as any)
        return data
    },

    async cancel(transactionId: string, reason?: string): Promise<void> {
        await axios.post(`${BASE_PAYMENTS}/${transactionId}/cancel`, { reason })
    },

    async resend(transactionId: string): Promise<void> {
        await axios.post(`${BASE_PAYMENTS}/${transactionId}/resend`)
    },

    async manualFallback(payload: ManualFallbackPayload): Promise<void> {
        await axios.post(`${BASE_PAYMENTS}/manual-fallback`, payload)
    },

    /**
     * Recupere la derniere transaction Revolut associee a un Order OandB.
     * Renvoie `null` si aucune transaction trouvee (commande non TPE).
     */
    async findByOrderId(orderId: string): Promise<IRevolutTransaction | null> {
        const { data } = await axios.get(`${BASE_PAYMENTS}/by-order/${orderId}`)
        if (data?.found === false) return null
        return data as IRevolutTransaction
    },

    /**
     * Construit l'URL publique du PDF du recu CB.
     *
     * La route API est publique mais protegee par un token (= order.token)
     * pour eviter l'enumeration. Permet donc d'utiliser un simple
     * <a download href="..."> ou window.open(url) sans gerer le Bearer.
     *
     * @param orderToken token de l'Order OandB (champ `Order.token`)
     */
    getReceiptPdfUrl(transactionId: string, orderToken: string): string {
        return `${import.meta.env.VITE_API_BASE_URL}${BASE_PAYMENTS}/${transactionId}/receipt/${orderToken}`
    },

    /**
     * Telecharge le PDF du recu CB en blob (via fetch sur l'URL publique).
     * Utilise pour l'impression via QZ Tray.
     */
    async getReceiptPdfBlob(
        transactionId: string,
        orderToken: string,
    ): Promise<Blob> {
        const { data } = await axios.get(
            `${BASE_PAYMENTS}/${transactionId}/receipt/${orderToken}`,
            { responseType: 'blob' } as any,
        )
        return data as Blob
    },

    /**
     * Envoie le recu CB par email au destinataire fourni.
     */
    async sendReceiptByEmail(
        transactionId: string,
        email: string,
    ): Promise<void> {
        await axios.post(`${BASE_PAYMENTS}/${transactionId}/receipt/email`, {
            email,
        })
    },
}
